import React, { useState, useMemo } from 'react'; // 添加useMemo优化
import {
  Box,
  Typography,
  Card,
  CardMedia,
  styled,
  useTheme,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getCharacters } from './data/characters';
import CharacterInfoWindow from './components/CharacterInfoWindow';
import { languages, useLanguage } from './data/languages';

const CharacterCard = styled(Card)(({ theme, isHovered, isDimmed, characterId }) => ({
  width: 'clamp(120px, 11vw, 200px)',
  aspectRatio: '3/5',
  flexShrink: 0,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[8],
  position: 'relative',
  transition: 'all 0.3s ease',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  border: '0px solid rgba(255, 255, 255, 0.2)',
  opacity: isDimmed ? 0.3 : 1,
  transform: isHovered 
    ? 'translateY(-10px)' 
    : characterId === 2 
      ? 'translateY(0px)'  // Special position for character ID 2
      : 'translateY(0)',
  '&:hover': {
    opacity: 1,
    boxShadow: theme.shadows[20],
    border: '4px solid rgba(255, 255, 255, 0.8)',
    borderRadius: '50px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: isHovered ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
    transition: 'all 0.3s ease',
    borderRadius: 'inherit',
  },
}));

const CharacterImage = styled(CardMedia)(({ theme, characterId, index }) => {
  let transform = 'translateY(0)';  // default state
  
  if (characterId === 1) {
    transform = 'scale(2) translateY(15px)';  // second character expands
  } else if (characterId === 2) {
    transform = ' scale(1.07) translateY(-0px)';  // character with ID 2 moves up
  } else if (characterId === 4) {
    transform = ' scale(1.07) ';  // character with ID 2 moves up
  }

  return {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
    transform,
    transition: 'all 0.3s ease',
    transformOrigin: 'center center',
  };
});

const AnimatedBackground = styled(Box)(({ colors }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: colors ? 
    colors.map((color, index) => 
      `radial-gradient(closest-side, ${color}, ${color}00)`
    ).join(',') :
    `
      radial-gradient(closest-side, #ece5d4, #ece5d400),
      radial-gradient(closest-side, #e6cda8, #e6cda800),
      radial-gradient(closest-side, #8dc1c9, #8dc1c900)
    `,
  backgroundSize: colors ?
    colors.map(() => '130vmax 130vmax').join(',') :
    '130vmax 130vmax, 130vmax 130vmax, 130vmax 130vmax',
  backgroundPosition: colors ?
    colors.map((_, index) => `${-30 + (index * 20)}vmax ${-30 + (index * 20)}vmax`).join(',') :
    '-60vmax -60vmax, 0vmax 0vmax, 60vmax 60vmax',
  backgroundRepeat: 'no-repeat',
  animation: 'movement 10s linear infinite',
  zIndex: -1,
  transition: 'all 0.5s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  '@keyframes movement': {
    '0%, 100%': {
      backgroundSize: '130vmax 130vmax, 80vmax 80vmax, 90vmax 90vmax, 110vmax 110vmax, 90vmax 90vmax',
      backgroundPosition: '-80vmax -80vmax, 60vmax -30vmax, 10vmax 10vmax, -30vmax -10vmax, 50vmax 50vmax',
    },
    '25%': {
      backgroundSize: '100vmax 100vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 60vmax 60vmax',
      backgroundPosition: '-60vmax -90vmax, 50vmax -40vmax, 0vmax -20vmax, -40vmax -20vmax, 40vmax 60vmax',
    },
    '50%': {
      backgroundSize: '80vmax 80vmax, 110vmax 110vmax, 80vmax 80vmax, 60vmax 60vmax, 80vmax 80vmax',
      backgroundPosition: '-50vmax -70vmax, 40vmax -30vmax, 10vmax 0vmax, 20vmax 10vmax, 30vmax 70vmax',
    },
    '75%': {
      backgroundSize: '90vmax 90vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 70vmax 70vmax',
      backgroundPosition: '-50vmax -40vmax, 50vmax -30vmax, 20vmax 0vmax, -10vmax 10vmax, 40vmax 60vmax',
    },
  },
}));

const CardTitle = styled(Box)(({ theme, isHovered }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  textAlign: 'center',
  width: '100%',
  padding: theme.spacing(2),
  opacity: isHovered ? 1 : 0,
  transition: 'all 0.3s ease',
  '& .MuiTypography-root': {
    color: '#fff',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
}));

const LinearLayout = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(10),
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  maxHeight: '50vh',
}));

const LinearCardWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  transition: 'all 0.3s ease-out',
}));

export default function NPCSelection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hoveredCharacter, setHoveredCharacter] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const currentLang = useLanguage(); // 响应式语言获取
  const config = useMemo(() => languages[currentLang] || languages['en'], [currentLang]); // 优化语言配置获取

  // Prevent scrolling on the document body
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleCardClick = (character) => {
    setSelectedCharacter(character);
  };

  const handleCharacterChange = (newCharacter) => {
    setSelectedCharacter(newCharacter);
  };

  return (
    <Box sx={{ 
      height: '100vh',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      pt: 0,
      zIndex: 0,
      overflow: 'hidden',
    }}>
      <AnimatedBackground colors={hoveredCharacter?.colors} />
      
      {!selectedCharacter && (
        <>
          <LinearLayout>
            {getCharacters(currentLang).map((character, index) => (
              <LinearCardWrapper key={character.id}>
                <CharacterCard
                  onClick={() => handleCardClick(character)}
                  onMouseEnter={() => setHoveredCharacter(character)}
                  onMouseLeave={() => setHoveredCharacter(null)}
                  isHovered={hoveredCharacter?.id === character.id}
                  isDimmed={hoveredCharacter && hoveredCharacter.id !== character.id}
                  characterId={character.id}
                  index={index}
                >
                  <picture>
                    <source srcset={character.image.replace('.png', '.jxl')} type="image/jxl" />
                    <source srcset={character.image.replace('.png', '.avif')} type="image/avif" />                    
                    <CharacterImage
                      component="img"
                      image={character.image}
                      alt={character.title}
                      characterId={character.id}
                      index={index}
                    />
                  </picture>
                  <CardTitle isHovered={hoveredCharacter?.id === character.id}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        mb: 1,
                      }}
                    >
                      {character.title}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: '1rem',
                        fontStyle: 'italic',
                        opacity: 0.9,
                        display: 'block',
                      }}
                    >
                      {character.subtitle}
                    </Typography>
                  </CardTitle>
                </CharacterCard>
              </LinearCardWrapper>
            ))}
          </LinearLayout>

          {/* Traits section */}
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            mt: 4,
            maxHeight: '35vh',
            overflow: 'hidden',
          }}>
            {hoveredCharacter ? (
              // Show traits when character is hovered
              hoveredCharacter.personality.map((trait, index) => (
                <Typography
                  key={index}
                  variant="h3"
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: 'rgba(0,0,0, 0.9)',
                    fontSize: 'clamp(2rem, 4vw, 4rem)',
                    fontWeight: 400,
                    opacity: 0,
                    animation: 'fadeIn 0.5s ease-out forwards',
                    animationDelay: `${index * 0.2}s`,
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateY(20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  {trait}
                </Typography>
              ))
            ) : (
              // Show placeholder when no character is hovered
              <Typography
                variant="h3"
                sx={{
                  marginTop: '2%',
                  fontFamily: "'Space Grotesk', sans-serif",
                  color: 'rgba(0,0,0, 0.6)',
                  fontSize: 'clamp(2rem, 4vw, 4rem)',
                  fontWeight: 400,
                  opacity: 0,
                  mt: 5,
                  animation: 'fadeIn 0.5s ease-out forwards',
                  '@keyframes fadeIn': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(20px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
{config.npcSelection.hoverReveal}
              </Typography>
            )}
          </Box>
        </>
      )}

      <CharacterInfoWindow
        open={selectedCharacter !== null}
        onClose={() => setSelectedCharacter(null)}
        character={selectedCharacter}
        onCharacterChange={handleCharacterChange}
      />
    </Box>
  );
}
 

